import { Edit2, MapPin, Phone, Mail, Trash2 } from 'lucide-react';
import { Button, Badge, cn } from '@shopmuchmore/design-system';
import type { ShippingAddress } from '../shipping.types';
import { DeleteAddressDialog } from './DeleteAddressDialog';

interface ShippingAddressItemProps {
  address: ShippingAddress;
  onEdit: (slug: string) => void;
  onSetDefault: (slug: string) => void;
  onDelete: (slug: string) => Promise<void>;
  isDeleting?: boolean;
  selected?:boolean;
}

export function ShippingAddressItem({ 
  address, 
  onEdit, 
  onSetDefault, 
  onDelete,
  isDeleting = false ,
  selected = false
}: ShippingAddressItemProps) {
  return (
    <div
      className={cn(
        "rounded-3xl border-[1.5px] border-border transition-all duration-200 flex flex-col h-full",
        (selected || address.is_default) && "border-secondary"
      )}
    >
      <div className="flex-1 min-w-0 p-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-bold text-gray-900 truncate">
              {address.first_name} {address.last_name}
            </h4>
            {address.is_default && (
              <Badge variant="success" className="shrink-0" size="sm">
                Default
              </Badge>
            )}
          </div>
          
          <div className="space-y-1.5">
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
              <span className="line-clamp-2">
                {address.street_address}, {address.city}, {address.state_or_region}, {address.postal_code}, {address.country}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone className="w-4 h-4 shrink-0 text-gray-400" />
              <span>{address.phone_number}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail className="w-4 h-4 shrink-0 text-gray-400" />
              <span className="truncate">{address.email}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className={cn("flex items-center gap-3 p-4 border-t-[1.5px] border-gray-100 mt-auto", (selected || address.is_default) && "border-secondary")}>
        {!address.is_default && (
          <Button
            variant="ghost"
            size="sm"
            className="text-secondary hover:text-secondary/80 font-medium px-0"
            onClick={(e) => {
              e.stopPropagation();
              onSetDefault(address.shipping_slug);
            }}
          >
            Set as Default
          </Button>
        )}
        <div className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
          <DeleteAddressDialog 
            onConfirm={() => onDelete(address.shipping_slug)}
            isLoading={isDeleting}
            trigger={
              <Button
                variant="destructive"
                size="sm"
                className="h-7 px-2.5 text-xs rounded-full gap-1.5 font-semibold"
                loading={isDeleting}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </Button>
            }
          />
          <Button
            variant="secondary"
            size="sm"
            className="h-7 px-2.5 text-xs rounded-full gap-1.5 font-semibold"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(address.shipping_slug);
            }}
          >
            <Edit2 className="w-3.5 h-3.5" />
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
}
